// Cruise Command · Editorial Candidate Review Panel
//
// Rendered ONLY when the Marketing Landing is loaded with an
// `?editorial_candidate=...` query string. Displays the exact
// production composition with the candidate photograph in place,
// plus a governance panel for Product Office ratification.
//
// The panel emits the ratification decision as a copy-to-clipboard
// JSON block that Engineering commits to editorialImages.js.  No
// backend endpoint is required — the human-in-the-loop is the
// governance boundary.
//
// Governance: LAW-PHOTO-001 · RC6-L03 · RC6-L04
import React, { useState, useMemo } from "react";

const PO_ACCENT = "#0f6a4d";
const PO_INK    = "#0b132b";
const PO_MUTED  = "#7a8299";

/**
 * Parse the current URL's query string into a candidate record.
 * Returns null if `?editorial_candidate` is absent, in which case
 * the Marketing Landing renders normally against the session library.
 */
export function parseCandidateFromLocation() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const url = params.get("editorial_candidate");
  if (!url) return null;
  const fp = (params.get("focal_point") || "50,50").split(",").map(Number);
  return {
    id:           params.get("id") || "candidate-preview",
    url,
    register:     params.get("register") || "unspecified",
    alt:          params.get("alt") || "Candidate photograph — awaiting Product Office review.",
    attribution:  params.get("attribution") || "Unattributed",
    focal_point:  { x: Number.isFinite(fp[0]) ? fp[0] : 50,
                    y: Number.isFinite(fp[1]) ? fp[1] : 50 },
    crop_hint:    params.get("crop_hint") || "",
    proposal_id:  params.get("proposal_id") || "",
    proposed_by:  params.get("proposed_by") || "Editorial",
  };
}


export default function CandidateReviewPanel({ candidate }) {
  const [decision, setDecision] = useState("hold");   // ratify | hold | reject
  const [note,     setNote]     = useState("");
  const [copied,   setCopied]   = useState(false);

  const decisionBlock = useMemo(() => {
    const label = { ratify: "RATIFIED", hold: "HOLD", reject: "REJECTED" }[decision];
    const now   = new Date().toISOString().slice(0, 10);
    const entry = {
      id:          candidate.id,
      url:         candidate.url,
      alt:         candidate.alt,
      attribution: candidate.attribution,
      register:    candidate.register,
      focal_point: candidate.focal_point,
      crop_hint:   candidate.crop_hint,
      added_at:    now,
      ratified_by: `PO · ${now}`,
    };
    return JSON.stringify({
      proposal_id:   candidate.proposal_id,
      proposed_by:   candidate.proposed_by,
      decision:      label,
      decided_at:    now,
      decided_by:    "Product Office",
      rationale:     note || null,
      library_entry: decision === "ratify" ? entry : null,
    }, null, 2);
  }, [decision, note, candidate]);

  const copy = () => {
    try {
      navigator.clipboard.writeText(decisionBlock);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { setCopied(false); }
  };

  return (
    <aside
      data-testid="cc-candidate-review-panel"
      style={{
        position:  "fixed",
        top:       "16px",
        right:     "16px",
        width:     "360px",
        maxHeight: "94vh",
        overflow:  "auto",
        background: "#ffffff",
        color:      PO_INK,
        borderRadius: "16px",
        boxShadow: "0 24px 60px rgba(0,0,0,0.32)",
        padding:   "18px 20px",
        zIndex:    9999,
        fontFamily: "'Source Serif 4', Georgia, serif",
        fontSize:  "13px",
        lineHeight: 1.5,
      }}
    >
      <div style={{
        fontSize: "9px", letterSpacing: "0.28em", textTransform: "uppercase",
        fontWeight: 900, color: PO_ACCENT, marginBottom: "6px",
      }}>
        Editorial Candidate · Preview
      </div>
      <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "20px", fontWeight: 500 }}>
        Ratify · Hold · Reject
      </div>

      <div style={{
        marginTop: "14px", paddingTop: "12px",
        borderTop: `1px solid ${PO_MUTED}40`,
        display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px",
        fontSize: "12px",
      }}>
        <span style={{ color: PO_MUTED }}>Proposal</span> <span>{candidate.proposal_id || "—"}</span>
        <span style={{ color: PO_MUTED }}>Proposed by</span> <span>{candidate.proposed_by}</span>
        <span style={{ color: PO_MUTED }}>Register</span>   <span>{candidate.register}</span>
        <span style={{ color: PO_MUTED }}>Attribution</span><span>{candidate.attribution}</span>
        <span style={{ color: PO_MUTED }}>Focal point</span><span>{candidate.focal_point.x}% · {candidate.focal_point.y}%</span>
        <span style={{ color: PO_MUTED }}>Crop hint</span>  <span>{candidate.crop_hint || "—"}</span>
        <span style={{ color: PO_MUTED }}>Alt</span>        <span style={{ fontStyle: "italic" }}>{candidate.alt}</span>
        <span style={{ color: PO_MUTED }}>URL</span>        <span style={{ wordBreak: "break-all", fontSize: "10.5px" }}>{candidate.url}</span>
      </div>

      <div style={{
        marginTop: "16px", paddingTop: "14px",
        borderTop: `1px solid ${PO_MUTED}40`,
      }}>
        <div style={{
          fontSize: "9px", letterSpacing: "0.28em", textTransform: "uppercase",
          fontWeight: 900, color: PO_ACCENT, marginBottom: "8px",
        }}>
          The Two Tests
        </div>
        <div style={{ fontSize: "12px", color: PO_INK, marginBottom: "4px" }}>
          <em>Coffee Table Test.</em> If full-page in a premium travel publication with no caption, would someone pause to admire it?
        </div>
        <div style={{ fontSize: "12px", color: PO_INK }}>
          <em>Cruise Command Test.</em> Does it make someone feel they are beginning a journey, rather than buying one?
        </div>
      </div>

      <div style={{ marginTop: "16px", display: "flex", gap: "6px" }}>
        {["ratify", "hold", "reject"].map((d) => (
          <button
            key={d}
            onClick={() => setDecision(d)}
            data-testid={`cc-candidate-decision-${d}`}
            style={{
              flex: 1,
              minHeight: "36px",
              borderRadius: "999px",
              fontSize: "11.5px",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              border: `1px solid ${decision === d ? PO_INK : `${PO_MUTED}66`}`,
              background: decision === d ? PO_INK : "transparent",
              color:      decision === d ? "#f5efe4" : PO_INK,
              cursor: "pointer",
            }}
          >{d}</button>
        ))}
      </div>

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid="cc-candidate-note"
        placeholder="Reason (optional for RATIFY, required for HOLD/REJECT)"
        rows={3}
        style={{
          marginTop: "10px",
          width: "100%",
          border: `1px solid ${PO_MUTED}66`,
          borderRadius: "10px",
          padding: "8px 10px",
          fontFamily: "inherit",
          fontSize: "12px",
          resize: "vertical",
        }}
      />

      <pre style={{
        marginTop: "12px",
        fontSize: "10.5px",
        background: "#f5efe4",
        border: `1px solid ${PO_MUTED}44`,
        borderRadius: "8px",
        padding: "10px",
        maxHeight: "180px",
        overflow: "auto",
        whiteSpace: "pre-wrap",
      }}>{decisionBlock}</pre>

      <button
        onClick={copy}
        data-testid="cc-candidate-copy"
        style={{
          marginTop: "10px",
          width: "100%",
          minHeight: "40px",
          borderRadius: "999px",
          background: PO_ACCENT,
          color: "#f5efe4",
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          border: "none",
          cursor: "pointer",
        }}
      >{copied ? "Copied — hand to Engineering" : "Copy decision block"}</button>

      <div style={{
        marginTop: "10px", fontSize: "10.5px", color: PO_MUTED, textAlign: "center",
      }}>
        Governance · LAW-PHOTO-001 · RC6-L04
      </div>
    </aside>
  );
}
